import {
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  CreditCardIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import Loading from "components/Loader/Loading";
import { AuthContext } from "context/appContext";
import { AnimatePresence, motion } from "framer-motion";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import React, { useContext, useEffect } from "react";
import { Helmet } from "react-helmet";
import { getCustomerSubscription, getSubscriptionDetails } from "redux/features/subscription/subscriptionSlice";
import CommonLayout from "./CommonLayout";
import ButtonSecondary from "shared/Button/ButtonSecondary";

interface BrandIcons {
  [key: string]: JSX.Element;
}

const brandIcons: BrandIcons = {
  visa: <CreditCardIcon className="flex-shrink-0 w-8 h-8 text-indigo-600" />,
  mastercard: <CreditCardIcon className="flex-shrink-0 w-8 h-8 text-indigo-600" />,
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const ClientSubscriptionPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const subscription = useAppSelector((state) => state.subscription.planData);
  const authContext = useContext(AuthContext);
  const profile = authContext.user?.profil;

  useEffect(() => {
    if (profile?.id) {
      dispatch(getCustomerSubscription());
    }
  }, [dispatch, profile?.id]);

  const getCustomerSubscriptionDetails = async () => {
    const res = await dispatch(getSubscriptionDetails());

    window.open(res.payload, "_blank");
  };

  if (!subscription) {
    return (
      <CommonLayout>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-6 text-center text-gray-600 rounded-lg bg-gray-50"
        >
          <Loading />
        </motion.div>
      </CommonLayout>
    );
  }

  const isCanceled = subscription.canceled_at !== undefined && subscription.canceled_at !== null;

  const statusColor = isCanceled
    ? "bg-red-100 text-red-800"
    : subscription.status === "trialing"
      ? "bg-blue-100 text-blue-800"
      : subscription.status === "active"
        ? "bg-green-100 text-green-800"
        : "bg-red-100 text-red-800";

  const statusText = isCanceled
    ? "Annulé"
    : subscription.status === "trialing"
      ? "En essai"
      : subscription.status === "active"
        ? "Actif"
        : "Inactif";
  return (
    <div className="nc-AccountPage" data-nc-id="AccountPage">
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>
      <CommonLayout>
        <AnimatePresence>
          <motion.div
            className="max-w-2xl mx-auto overflow-hidden bg-white border border-gray-200 shadow-xl rounded-2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            <div className="p-8 bg-eventToBee">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold text-white">{subscription.plan.name}</h2>
                <span
                  className={`px-4 py-1 rounded-full text-sm font-semibold ${statusColor} transition-transform duration-300 hover:scale-105`}
                >
                  {statusText}
                </span>
              </div>
            </div>
            <div className="p-8 space-y-6">
              <motion.div
                className="grid grid-cols-1 gap-6 sm:grid-cols-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ staggerChildren: 0.2 }}
              >
                <motion.div
                  className="flex items-start p-4 space-x-4 transition-colors duration-200 rounded-lg bg-gray-50 hover:bg-gray-100"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <CreditCardIcon className="flex-shrink-0 w-8 h-8 text-indigo-600" />
                  <div>
                    <p className="text-sm text-gray-600">Montant</p>
                    <p className="text-base font-semibold text-gray-900">
                      {subscription.plan.amount} {subscription.plan.currency.toUpperCase()} /{" "}
                      {subscription.plan.interval === "month" ? "mois" : "an"}
                    </p>
                  </div>
                </motion.div>
                <motion.div
                  className="flex items-start p-4 space-x-4 transition-colors duration-200 rounded-lg bg-gray-50 hover:bg-gray-100"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <CalendarIcon className="flex-shrink-0 w-8 h-8 text-indigo-600" />
                  <div>
                    <p className="text-sm text-gray-600">Date de début</p>
                    <p className="text-base font-semibold text-gray-900">{formatDate(subscription.start_date)}</p>
                  </div>
                </motion.div>

                {subscription.cancel_at && (
                  <motion.div
                    className="flex items-start p-4 space-x-4 transition-colors duration-200 rounded-lg bg-gray-50 hover:bg-gray-100"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                  >
                    <CalendarIcon className="flex-shrink-0 w-8 h-8 text-indigo-600" />
                    <div>
                      <p className="text-sm text-gray-600">Annulation prévue</p>
                      <p className="text-base font-semibold text-gray-900">
                        {new Date(subscription.cancel_at * 1000).toLocaleDateString("fr-FR", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </p>
                    </div>
                  </motion.div>
                )}

                {subscription.cancel_at && (
                  <motion.div
                    className="flex items-start p-4 space-x-4 transition-colors duration-200 rounded-lg bg-gray-50 hover:bg-gray-100"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                  >
                    <CalendarIcon className="flex-shrink-0 w-8 h-8 text-indigo-600" />
                    <div>
                      <p className="text-sm text-gray-600">Annulation à la fin de la période d'essai</p>
                      <p className="text-base font-semibold text-gray-900">
                        {subscription.cancel_at_period_end === "false" ? "Non" : "Oui"}
                      </p>
                    </div>
                  </motion.div>
                )}

                {subscription.canceled_at && (
                  <motion.div
                    className="flex items-start p-4 space-x-4 transition-colors duration-200 rounded-lg bg-gray-50 hover:bg-gray-100"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                  >
                    <CalendarIcon className="flex-shrink-0 w-8 h-8 text-indigo-600" />
                    <div>
                      <p className="text-sm text-gray-600">Annulé le</p>
                      <p className="text-base font-semibold text-gray-900">
                        {new Date(subscription.canceled_at * 1000).toLocaleDateString("fr-FR", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </p>
                    </div>
                  </motion.div>
                )}

                {subscription.trial_end && (
                  <motion.div
                    className="flex items-start p-4 space-x-4 transition-colors duration-200 rounded-lg bg-gray-50 hover:bg-gray-100"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                  >
                    <ClockIcon className="flex-shrink-0 w-8 h-8 text-indigo-600" />
                    <div>
                      <p className="text-sm text-gray-600">Date de fin </p>
                      <p className="text-base font-semibold text-gray-900">{formatDate(subscription.trial_end)}</p>
                    </div>
                  </motion.div>
                )}

                <motion.div
                  className="flex items-start p-4 space-x-4 transition-colors duration-200 rounded-lg bg-gray-50 hover:bg-gray-100"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <ClockIcon className="flex-shrink-0 w-8 h-8 text-indigo-600" />
                  <div>
                    <p className="text-sm text-gray-600">Prochaine facture estimée </p>
                    <p className="text-base font-semibold text-gray-900">
                      {formatDate(subscription.current_period_end)}
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  className="flex items-start p-4 space-x-4 transition-colors duration-200 rounded-lg bg-gray-50 hover:bg-gray-100"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  {subscription.cancel_at_period_end === true ? (
                    <XCircleIcon className="flex-shrink-0 w-8 h-8 text-red-500" />
                  ) : (
                    <CheckCircleIcon className="flex-shrink-0 w-8 h-8 text-green-500" />
                  )}
                  <div>
                    <p className="text-sm text-gray-600">Renouvellement automatique</p>
                    <p className="text-base font-semibold text-gray-900">
                      {subscription.cancel_at_period_end === true ? "Désactivé" : "Actif"}
                    </p>
                  </div>
                </motion.div>
                <motion.div
                  className="flex items-start p-4 space-x-4 transition-colors duration-200 border border-gray-100 rounded-lg shadow-sm bg-gray-50 hover:bg-gray-100"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  {subscription.payment_method && brandIcons[subscription.payment_method.brand] ? (
                    brandIcons[subscription.payment_method.brand]
                  ) : (
                    <CreditCardIcon className="flex-shrink-0 w-8 h-8 text-indigo-600" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-600">Méthode de paiement</p>

                    <p
                      className="relative text-lg font-semibold text-gray-900 group"
                      aria-label={`Méthode de paiement : ${subscription.payment_method.brand}`}
                    >
                      {subscription.payment_method.brand.charAt(0).toUpperCase() +
                        subscription.payment_method.brand.slice(1)}{" "}
                    </p>
                  </div>
                </motion.div>
              </motion.div>

              <hr />
              <div className="text-sm text-center text-neutral-500">
                <ButtonSecondary onClick={getCustomerSubscriptionDetails}>Plus details</ButtonSecondary>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </CommonLayout>
    </div>
  );
};

export default ClientSubscriptionPage;
